import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, inject } from '@angular/core';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { provideRouter, withComponentInputBinding, withEnabledBlockingInitialNavigation } from '@angular/router';
import { ApolloLink, InMemoryCache } from '@apollo/client';
import { AppUpdateLoggerService } from '@h5s/feature-log';
import { NON_RESTORABLE_ROUTES } from '@h5s/util-auth';
import { provideBroadcastChannel } from '@h5s/util-broadcast';
import { provideAppVersion } from '@h5s/util-config';
import { LoggerService, LOGGING_CONFIG_TOKEN, RollbarService } from '@h5s/util-logger';
import { initDecoratorsConfig } from '@h5s/util-object';
import { providePwaUpdateInitializer, PWA_UPDATE_LOGGER_TOKEN } from '@h5s/util-pwa';
import { TRANSLATION_CONFIG_TOKEN } from '@h5s/util-translate';
import { OAuthStorage, provideOAuthClient } from 'angular-oauth2-oidc';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { environment } from '../environments/environment';
import { APP_VERSION } from '../environments/version';
import { appRoutes } from './app.routes';
import { apolloGraphQLFormattedErrorInterceptor } from './errors/apollo-graphql-error.graphql.interceptor';
import { apolloNetworkErrorInterceptor } from './errors/apollo-network-error.graphql.interceptor';
import { ErrorService } from './errors/error.service';
import { GlobalErrorHandler } from './errors/global-error-handler.service';
import { httpErrorInterceptor } from './errors/http-error.interceptor';
import { authInterceptor } from './interceptors/auth.interceptor';
import { pratInterceptor } from './interceptors/prat.interceptor';
import { restInterceptor } from './interceptors/rest.interceptor';
import { authInitializerProvider } from './providers/auth-initializer';
import { matIconInitializerProvider } from './providers/mat-icon-initializer';
import { oAuthStorageFactory } from './providers/oauth-storage-factory';
import { rollbarFactory } from './providers/rollbar-factory';
import { provideRouteReuse } from './providers/route-reuse';
import { zBridgeVersionInitializerProvider } from './providers/z-bridge-version-initializer';

initDecoratorsConfig({
  isDevelop: environment.environment === 'development',
});

/** Rotte di servizio da non ripristinare dopo il login: l'utente ne sta appena uscendo. */
const NON_RESTORABLE_SERVICE_ROUTES: readonly string[] = ['/pick-your-place'];

/** Rotte di servizio senza tab, ricreate da zero a ogni ingresso. */
const TRANSIENT_SERVICE_ROUTES: readonly string[] = ['/pick-your-place'];

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(appRoutes, withEnabledBlockingInitialNavigation(), withComponentInputBinding()),
    provideOAuthClient({
      /**
       * Configurazione del server di autenticazione
       * Se sendAccessToken true, invia l'access_token verso urls presenti in allowedUrls
       */
      resourceServer: {
        allowedUrls: [environment.apiConfig.graphqlUrl, environment.apiConfig.restUrl, environment.apiConfig.uasUrl],
        sendAccessToken: true,
      },
    }),
    provideHttpClient(withInterceptors([restInterceptor, pratInterceptor, httpErrorInterceptor, authInterceptor])),
    provideApollo(() => {
      const httpClient = inject(HttpClient);
      const httpLink = new HttpLink(httpClient);

      const loggerService = inject(LoggerService);
      const errorService = inject(ErrorService);
      const errorGraphqlLink = apolloGraphQLFormattedErrorInterceptor(loggerService, errorService);
      const errorNetworkLink = apolloNetworkErrorInterceptor(loggerService, errorService);

      return {
        // Settiamo le opzioni di default delle query
        defaultOptions: {
          query: {
            fetchPolicy: 'no-cache',
            errorPolicy: 'none',
          },
          watchQuery: {
            fetchPolicy: 'no-cache',
            errorPolicy: 'none',
          },
          mutate: {
            errorPolicy: 'none',
          },
        },
        cache: new InMemoryCache(),
        link: ApolloLink.from([
          errorGraphqlLink,
          errorNetworkLink,
          httpLink.create({
            uri: environment.apiConfig.graphqlUrl,
          }),
        ]),
      };
    }),
    matIconInitializerProvider,
    authInitializerProvider,
    zBridgeVersionInitializerProvider,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: RollbarService, useFactory: rollbarFactory },
    { provide: OAuthStorage, useFactory: oAuthStorageFactory },
    { provide: LOGGING_CONFIG_TOKEN, useValue: environment.loggingConfig },
    { provide: NON_RESTORABLE_ROUTES, useValue: NON_RESTORABLE_SERVICE_ROUTES },
    { provide: TRANSLATION_CONFIG_TOKEN, useValue: environment.translationConfig },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: environment.toastConfig },
    { provide: PWA_UPDATE_LOGGER_TOKEN, useExisting: AppUpdateLoggerService },
    provideAppVersion(APP_VERSION),
    providePwaUpdateInitializer(),
    provideBroadcastChannel('pmsweb-broadcast-channel'),
    provideRouteReuse(TRANSIENT_SERVICE_ROUTES),
  ],
};
